import { useEffect, useMemo, useRef, useState } from 'react';
import { getData } from '../../../DataAccessLayer';
import { ats } from '../../../DataAccessLayer/services';
import { Box, LinearProgress, Typography } from '@mui/material';
import Filter from './Filters';
import PortalDataGridPro from '../../common/PortalDataGridPro';
import { getColumns } from './columns';
import PortalAlert from '../../common/PortalAlert';
import ApiErrorMessage from '../../common/ApiErrorMessage';

export default function ActionHistoryDetails(props) {
  const id = props.id;
  const pageSize = 20;
  const filtersInit = {
    description: '',
    action: '',
    status: '',
    midas: '',
  };
  const [, setData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [filters, setFilters] = useState(filtersInit);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize,
  });

  const prevFiltersRef = useRef(filters); // Track previous filters
  const prevPaginationModelRef = useRef(paginationModel); // Track pagination model

  const dataCache = useRef([]);
  const keyCache = useRef([]);

  const rowCountRef = useRef(totalCount || 0);

  const rowCount = useMemo(() => {
    if (totalCount !== undefined) {
      rowCountRef.current = totalCount;
    }
    return rowCountRef.current;
  }, [totalCount]);

  const reset = () => {
    setPaginationModel({ ...paginationModel, page: 0 });
    dataCache.current = [];
    keyCache.current = [];
  };

  useEffect(() => {
    loadActionHistory();
  }, []);

  // Clear the cache on page size change, or
  useEffect(() => {
    const prevPaginationModel = prevPaginationModelRef.current;
    const prevFilters = prevFiltersRef.current;

    // Fetch the new page data when the page size doesn't change, but page number changes
    // Fetch new data when the filters change
    if (
      prevPaginationModel.pageSize === paginationModel.pageSize &&
      prevPaginationModel.page !== paginationModel.page
    ) {
      loadActionHistory();
    }

    // If the page size changes, set the page number to 0, and clear the data cache
    if (
      (prevPaginationModel.pageSize !== paginationModel.pageSize &&
        prevPaginationModel.page === paginationModel.page) ||
      !_.isEqual(filters, prevFilters)
    ) {
      reset();
      loadActionHistory();
    }

    // Update prevFiltersRef to the current filters after the effect runs
    prevFiltersRef.current = filters;

    // Update prevPaginationModelRef to the current paginationModel after the effect runs
    prevPaginationModelRef.current = paginationModel;
  }, [filters, paginationModel]);

  const updateFilters = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const toggleIsLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  const toggleIsError = (value) => {
    setIsError(value);
  };

  const loadActionHistory = async () => {
    try {
      toggleIsLoading(true);
      toggleIsError(false);
      const newPage = paginationModel.page;
      const cachedPageData = dataCache.current[newPage];

      // If the page data is already cached, use it
      if (cachedPageData) {
        setData(cachedPageData);
        setCurrentData(cachedPageData);
      } else {
        const previousPageKey = keyCache.current[newPage - 1];
        const { description, action, status, midas } = filters;
        const params = {
          limit: paginationModel.pageSize,
          lastEvaluatedKey: newPage > 0 && previousPageKey ? previousPageKey : null,
          page: newPage,
          description,
          midas,
          action,
          status,
          termCode: props?.termCode,
          crn: props?.crn,
        };
        const data = await getData(ats.actionLogs(params), true);

        // Cache the data and lastEvaluatedKey
        dataCache.current[newPage] = data.items;
        keyCache.current[newPage] = data.lastEvaluatedKey;
        setData(data.items);
        setCurrentData(data.items);
        setTotalCount(data.totalCount);
      }
    } catch (error) {
      console.log(error);
      toggleIsError(true);
    } finally {
      toggleIsLoading(false);
    }
  };

  return (
    <>
      <Filter
        id={`${id}Filter`}
        crn={props.crn}
        termCode={props.termCode}
        updateFilters={updateFilters}
        showClearFieldsButton={true}
        filters={filters}
      />

      {!isLoading && isError && (
        <Box mt={2}>
          <PortalAlert id={`${id}__portalAlert`} severity="error">
            <ApiErrorMessage
              id={`${id}__apiErrorMessage`}
              reload={loadActionHistory}
              widgetName="Action History"
              isPage={true}
            />
          </PortalAlert>
        </Box>
      )}

      <Box mt={2} id={`${id}__box_dataGridWrapper`}>
        <PortalDataGridPro
          className="myOdu__resultTable"
          id={`${id}__dataGrid`}
          columns={getColumns(id)}
          sx={{
            '& .MuiDataGrid-cell--pinnedLeft': {
              zIndex: '10 !important',
            },
            '& .MuiDataGrid-cell': {
              whiteSpace: 'nowrap !important',
              minHeight: '3rem',
              alignContent: 'center',
            },
          }}
          rows={currentData}
          rowCount={rowCount}
          getRowId={(row) => row.MIDAS + row.CREATED_AT}
          checkboxSelection={false}
          disableRowSelectionOnClick
          pageSizeOptions={[20]}
          isLoading={isLoading}
          slots={{
            loadingOverlay: LinearProgress,
            noRowsOverlay: () => {
              return (
                <Box
                  mt={1}
                  display="flex"
                  justifyContent="center"
                  id={`${id}__box_noResultsWrapper`}
                >
                  <Typography id={`${id}__p_noResults`} variant="p" component="p">
                    No action history found.
                  </Typography>
                </Box>
              );
            },
          }}
          autoHeight
          initialState={{
            sorting: {
              sortModel: [{ field: 'CREATED_AT', sort: 'desc' }],
            },
          }}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          pagination
          getRowHeight={(params) => {
            return 'auto';
          }}
        />
      </Box>
    </>
  );
}
